/**

 * Assessment Engine API client — templates, campaigns, public respondent, reports.

 */

import api from './api';

import type {

  AeCampaign,

  AeCampaignAdmin,

  AeCampaignAdminCandidate,

  AeCampaignFunnel,

  AeCampaignInvite,

  AeCampaignLink,

  AeCampaignListItem,

  AeCampaignListParams,

  AeCampaignReport,

  AeCampaignSeries,

  AeCohortSettings,

  AeEvaluatedSection,

  AeInstantResults,

  AeCompleteResponseResult,

  AeLiveReport,

  AeNarrativeState,

  AePublicCampaign,

  AePreviewSessionResult,

  AeReportConfig,

  AeResponseSummary,

  AeResponseDetail,

  AeSessionStartResult,

  AeShareLink,

  AeTemplateDetail,

  AeTemplateSummary,

  CreateCampaignInput,

  CreateQuestionInput,

  CreateQuestionOptionInput,

  CreateSectionInput,

  CreateSeriesInput,

  CreateTemplateInput,

} from '../types/assessmentEngine';



const BASE = '/assessment-engine';



export const assessmentEngineService = {

  // --- Templates (platform admin) ---

  async listTemplates(status?: string): Promise<AeTemplateSummary[]> {

    const res = await api.get(`${BASE}/templates`, { params: status ? { status } : {} });

    return res.data.data;

  },



  async getTemplate(templateId: string): Promise<AeTemplateDetail> {

    const res = await api.get(`${BASE}/templates/${templateId}`);

    return res.data.data;

  },



  async createTemplate(input: CreateTemplateInput): Promise<AeTemplateSummary> {

    const res = await api.post(`${BASE}/templates`, input);

    return res.data.data;

  },



  async updateTemplate(

    templateId: string,

    patch: Partial<CreateTemplateInput>

  ): Promise<AeTemplateSummary> {

    const res = await api.put(`${BASE}/templates/${templateId}`, patch);

    return res.data.data;

  },



  async publishTemplate(templateId: string): Promise<AeTemplateSummary> {

    const res = await api.post(`${BASE}/templates/${templateId}/publish`);

    return res.data.data;

  },



  async retireTemplate(templateId: string): Promise<AeTemplateSummary> {

    const res = await api.post(`${BASE}/templates/${templateId}/retire`);

    return res.data.data;

  },



  async republishTemplate(templateId: string): Promise<AeTemplateSummary> {

    const res = await api.post(`${BASE}/templates/${templateId}/republish`);

    return res.data.data;

  },



  async cloneTemplate(

    templateId: string,

    body: { code: string; name: string }

  ): Promise<AeTemplateSummary> {

    const res = await api.post(`${BASE}/templates/${templateId}/clone`, body);

    return res.data.data;

  },



  async deleteTemplate(templateId: string): Promise<void> {

    await api.delete(`${BASE}/templates/${templateId}`);

  },

  async previewTemplate(
    templateId: string,
    answers: Record<string, string | number | boolean | null> = {}
  ): Promise<AePreviewSessionResult> {
    const res = await api.post(`${BASE}/templates/${templateId}/preview`, { answers });
    return res.data.data;
  },



  async createSection(templateId: string, input: CreateSectionInput) {

    const res = await api.post(`${BASE}/templates/${templateId}/sections`, input);

    return res.data.data;

  },



  async updateSection(templateId: string, sectionId: string, patch: Partial<CreateSectionInput>) {

    const res = await api.put(`${BASE}/templates/${templateId}/sections/${sectionId}`, patch);

    return res.data.data;

  },



  async createQuestion(templateId: string, input: CreateQuestionInput) {

    const res = await api.post(`${BASE}/templates/${templateId}/questions`, input);

    return res.data.data;

  },



  async updateQuestion(

    templateId: string,

    questionId: string,

    patch: Partial<CreateQuestionInput>

  ) {

    const res = await api.put(`${BASE}/templates/${templateId}/questions/${questionId}`, patch);

    return res.data.data;

  },



  async addOption(

    templateId: string,

    questionId: string,

    input: CreateQuestionOptionInput

  ) {

    const res = await api.post(

      `${BASE}/templates/${templateId}/questions/${questionId}/options`,

      input

    );

    return res.data.data;

  },



  async replaceQuestionOptions(

    templateId: string,

    questionId: string,

    options: CreateQuestionOptionInput[]

  ) {

    const res = await api.put(

      `${BASE}/templates/${templateId}/questions/${questionId}/options`,

      { options }

    );

    return res.data.data;

  },



  async deleteSection(templateId: string, sectionId: string): Promise<void> {

    await api.delete(`${BASE}/templates/${templateId}/sections/${sectionId}`);

  },



  async copySection(templateId: string, sectionId: string) {

    const res = await api.post(`${BASE}/templates/${templateId}/sections/${sectionId}/copy`);

    return res.data.data;

  },



  async moveSection(
    templateId: string,
    sectionId: string,
    body: { parent_section_id?: string | null; insert_index: number }
  ): Promise<void> {
    await api.post(`${BASE}/templates/${templateId}/sections/${sectionId}/move`, body);
  },



  async deleteQuestion(templateId: string, questionId: string): Promise<void> {

    await api.delete(`${BASE}/templates/${templateId}/questions/${questionId}`);

  },



  async copyQuestion(templateId: string, questionId: string) {

    const res = await api.post(`${BASE}/templates/${templateId}/questions/${questionId}/copy`);

    return res.data.data;

  },



  async moveQuestion(
    templateId: string,
    questionId: string,
    body: { section_id: string; insert_index: number }
  ): Promise<void> {
    await api.post(`${BASE}/templates/${templateId}/questions/${questionId}/move`, body);
  },



  async createLogicRule(templateId: string, body: Record<string, unknown>) {
    const res = await api.post(`${BASE}/templates/${templateId}/logic-rules`, body);
    return res.data.data;
  },

  async deleteLogicRule(templateId: string, ruleId: string): Promise<void> {
    await api.delete(`${BASE}/templates/${templateId}/logic-rules/${ruleId}`);
  },



  // --- Engine settings (platform admin) ---

  async getCohortSettings(): Promise<AeCohortSettings> {

    const res = await api.get(`${BASE}/settings/cohorts`);

    return res.data.data;

  },



  async updateCohortSettings(settings: AeCohortSettings): Promise<AeCohortSettings> {

    const res = await api.put(`${BASE}/settings/cohorts`, settings);

    return res.data.data;

  },



  // --- Series (tenant admin) ---

  async listSeries(): Promise<AeCampaignSeries[]> {

    const res = await api.get(`${BASE}/series`);

    return res.data.data;

  },



  async createSeries(input: CreateSeriesInput): Promise<AeCampaignSeries> {

    const res = await api.post(`${BASE}/series`, input);

    return res.data.data;

  },



  async updateSeries(

    seriesId: string,

    patch: Partial<CreateSeriesInput> & { is_active?: boolean }

  ): Promise<AeCampaignSeries> {

    const res = await api.put(`${BASE}/series/${seriesId}`, patch);

    return res.data.data;

  },



  // --- Campaigns (tenant admin) ---

  async listCampaigns(params?: AeCampaignListParams): Promise<AeCampaignListItem[]> {

    const res = await api.get(`${BASE}/campaigns`, { params: params ?? {} });

    const data = res.data?.data;

    return Array.isArray(data) ? data : [];

  },



  async getCampaign(campaignId: string): Promise<AeCampaign> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}`);

    return res.data.data;

  },

  async previewCampaign(
    campaignId: string,
    answers: Record<string, string | number | boolean | null> = {}
  ): Promise<AePreviewSessionResult> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/preview`, { answers });
    return res.data.data;
  },



  async createCampaign(input: CreateCampaignInput): Promise<AeCampaign> {

    const res = await api.post(`${BASE}/campaigns`, input);

    return res.data.data;

  },



  async updateCampaign(campaignId: string, patch: Partial<CreateCampaignInput>): Promise<AeCampaign> {

    const res = await api.put(`${BASE}/campaigns/${campaignId}`, patch);

    return res.data.data;

  },

  async deleteCampaign(campaignId: string): Promise<void> {
    await api.delete(`${BASE}/campaigns/${campaignId}`);
  },



  async openCampaign(campaignId: string): Promise<{ campaign: AeCampaign; invitesSent: number }> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/open`);
    return {
      campaign: res.data.data,
      invitesSent: Number(res.data?.meta?.invites_sent ?? 0),
    };
  },

  async closeCampaign(campaignId: string): Promise<AeCampaign> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/close`);
    return res.data.data;
  },

  async reopenCampaign(campaignId: string): Promise<{ campaign: AeCampaign; invitesSent: number }> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/reopen`);
    return {
      campaign: res.data.data,
      invitesSent: Number(res.data?.meta?.invites_sent ?? 0),
    };
  },

  async listCampaignAdmins(campaignId: string): Promise<AeCampaignAdmin[]> {
    const res = await api.get(`${BASE}/campaigns/${campaignId}/admins`);
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async listCampaignAdminCandidates(
    campaignId: string,
    search?: string
  ): Promise<AeCampaignAdminCandidate[]> {
    const res = await api.get(`${BASE}/campaigns/${campaignId}/admin-candidates`, {
      params: search ? { search } : {},
    });
    return Array.isArray(res.data?.data) ? res.data.data : [];
  },

  async addCampaignAdmin(campaignId: string, userId: string): Promise<AeCampaignAdmin> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/admins`, { user_id: userId });
    return res.data.data;
  },

  async removeCampaignAdmin(campaignId: string, userId: string): Promise<void> {
    await api.delete(`${BASE}/campaigns/${campaignId}/admins/${userId}`);
  },



  async getCampaignFunnel(campaignId: string): Promise<AeCampaignFunnel> {
    const res = await api.get(`${BASE}/campaigns/${campaignId}/funnel`);
    return res.data.data;
  },

  async getCampaignLink(campaignId: string): Promise<AeCampaignLink> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/link`);

    return res.data.data;

  },



  async bulkInvites(
    campaignId: string,
    emails: string[]
  ): Promise<{ inserted: number; emails_sent?: number; emails_deferred?: boolean }> {

    const res = await api.post(`${BASE}/campaigns/${campaignId}/invites`, { emails });

    return res.data.data;

  },



  async listInvites(campaignId: string): Promise<AeCampaignInvite[]> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/invites`);

    return res.data.data;

  },

  async removeInvite(campaignId: string, inviteId: string): Promise<void> {
    await api.delete(`${BASE}/campaigns/${campaignId}/invites/${inviteId}`);
  },

  async enableInvite(campaignId: string, inviteId: string): Promise<void> {
    await api.post(`${BASE}/campaigns/${campaignId}/invites/${inviteId}/enable`);
  },



  async listResponses(campaignId: string): Promise<AeResponseSummary[]> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/responses`);

    return res.data.data;

  },

  async invalidateResponse(campaignId: string, responseId: string): Promise<AeResponseSummary> {
    const res = await api.post(
      `${BASE}/campaigns/${campaignId}/responses/${responseId}/invalidate`
    );
    return res.data.data;
  },

  async restoreResponse(campaignId: string, responseId: string): Promise<AeResponseSummary> {
    const res = await api.post(
      `${BASE}/campaigns/${campaignId}/responses/${responseId}/restore`
    );
    return res.data.data;
  },

  async getCampaignResponseDetail(
    campaignId: string,
    responseId: string
  ): Promise<AeResponseDetail> {
    const res = await api.get(`${BASE}/campaigns/${campaignId}/responses/${responseId}`);
    return res.data.data;
  },

  async requestRespondentOtp(linkToken: string, email: string): Promise<void> {
    await api.post(`${BASE}/public/${linkToken}/otp/request`, { email });
  },

  async verifyRespondentOtp(
    linkToken: string,
    email: string,
    code: string
  ): Promise<void> {
    await api.post(`${BASE}/public/${linkToken}/otp/verify`, { email, code });
  },



  async getCampaignReport(

    campaignId: string,

    filters?: Record<string, string>

  ): Promise<AeCampaignReport> {

    const params: Record<string, string> = {};

    if (filters) {

      for (const [k, v] of Object.entries(filters)) {

        if (v.trim()) params[`filter_${k}`] = v.trim();

      }

    }

    const res = await api.get(`${BASE}/campaigns/${campaignId}/reports`, { params });

    return res.data.data;

  },



  async getReportConfig(campaignId: string): Promise<AeReportConfig | null> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/reports/config`);

    return res.data.data;

  },



  async updateReportConfig(

    campaignId: string,

    patch: Partial<AeReportConfig>

  ): Promise<AeReportConfig> {

    const res = await api.put(`${BASE}/campaigns/${campaignId}/reports/config`, patch);

    return res.data.data;

  },



  async exportReportPdf(
    campaignId: string,
    options?: {
      filters?: Record<string, string>;
      narrative_text?: string | null;
    }
  ): Promise<Blob> {
    const res = await api.post(
      `${BASE}/campaigns/${campaignId}/reports/exports`,
      {
        filters: options?.filters ?? {},
        narrative_text: options?.narrative_text ?? null,
      },
      {
        responseType: 'blob',
      }
    );
    return res.data;
  },



  async getNarrative(campaignId: string): Promise<AeNarrativeState> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/reports/narrative`);

    return res.data.data;

  },



  async refreshNarrative(campaignId: string, useAi = false): Promise<AeNarrativeState> {

    const res = await api.post(`${BASE}/campaigns/${campaignId}/reports/narrative/refresh`, {

      use_ai: useAi,

    });

    return res.data.data;

  },



  async publishNarrative(campaignId: string, published_text: string): Promise<AeNarrativeState> {

    const res = await api.put(`${BASE}/campaigns/${campaignId}/reports/narrative/publish`, {

      published_text,

    });

    return res.data.data;

  },



  async createShareLink(

    campaignId: string,

    body: { recipient_email?: string; viewer_scope?: Record<string, unknown>; expires_at?: string }

  ): Promise<AeShareLink> {

    const res = await api.post(`${BASE}/campaigns/${campaignId}/reports/share-links`, body);

    return res.data.data;

  },



  async listShareLinks(campaignId: string): Promise<AeShareLink[]> {

    const res = await api.get(`${BASE}/campaigns/${campaignId}/reports/share-links`);

    return res.data.data;

  },



  async revokeShareLink(campaignId: string, shareId: string): Promise<void> {

    await api.delete(`${BASE}/campaigns/${campaignId}/reports/share-links/${shareId}`);

  },



  // --- Public report viewer (no JWT on api instance — uses fetch) ---

  async requestReportOtp(shareToken: string, email: string): Promise<void> {

    const base = api.defaults.baseURL ?? '';

    await fetch(`${base}${BASE}/report-access/${shareToken}/otp/request`, {

      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ email }),

    });

  },



  async verifyReportOtp(

    shareToken: string,

    email: string,

    code: string

  ): Promise<{ session_token: string }> {

    const base = api.defaults.baseURL ?? '';

    const res = await fetch(`${base}${BASE}/report-access/${shareToken}/otp/verify`, {

      method: 'POST',

      headers: { 'Content-Type': 'application/json' },

      body: JSON.stringify({ email, code }),

    });

    const json = await res.json();

    if (!res.ok) throw new Error(json.message ?? 'OTP verification failed');

    return json.data;

  },



  async getLiveReport(shareToken: string, sessionToken: string): Promise<AeLiveReport> {

    const base = api.defaults.baseURL ?? '';

    const res = await fetch(`${base}${BASE}/report-access/${shareToken}/live`, {

      headers: { 'X-Report-Session': sessionToken },

    });

    const json = await res.json();

    if (!res.ok) throw new Error(json.message ?? 'Failed to load report');

    return json.data;

  },



  liveReportUrl(shareToken: string): string {

    return `/assessment/report/${shareToken}`;

  },



  // --- Public respondent (no auth required) ---

  async getPublicCampaign(linkToken: string): Promise<AePublicCampaign> {

    const res = await api.get(`${BASE}/public/${linkToken}`);

    return res.data.data;

  },

  /** Records invite click when respondent opens take URL from email (REQ-AE-036). */
  async recordInviteClick(linkToken: string, email: string): Promise<void> {
    await api.post(`${BASE}/public/${linkToken}/invite-click`, { email });
  },



  async startSession(

    linkToken: string,

    body: { email: string; demographics?: Record<string, unknown>; captcha_token?: string }

  ): Promise<AeSessionStartResult> {

    const res = await api.post(`${BASE}/public/${linkToken}/session`, body);

    return res.data.data;

  },



  async getPublicAnswers(linkToken: string, responseId: string): Promise<unknown[]> {

    const res = await api.get(`${BASE}/public/${linkToken}/responses/${responseId}/answers`);

    return res.data.data;

  },



  async upsertAnswer(
    linkToken: string,
    responseId: string,
    body: {
      question_id: string;
      answer_text?: string | null;
      answer_numeric?: number | null;
      answer_values?: string[] | null;
      /** Matrix statement scores: row key → number | 'NA' (stored as answer_json). */
      answer_matrix?: Record<string, number | 'NA'> | null;
      answer_json?: Record<string, unknown> | null;
      is_na?: boolean;
    }
  ): Promise<{
    resume_question_key: string | null;
    answer_map?: Record<string, string | number | boolean | null>;
    sections?: AeEvaluatedSection[];
  }> {
    const { answer_matrix, answer_values, ...rest } = body;
    const payload = {
      ...rest,
      answer_values: answer_values ?? undefined,
      answer_json:
        body.answer_json ??
        answer_matrix ??
        (answer_values && answer_values.length > 0 ? answer_values : null),
    };
    const res = await api.put(`${BASE}/public/${linkToken}/responses/${responseId}/answers`, payload);
    return res.data.data;
  },



  async completeResponse(

    linkToken: string,

    responseId: string

  ): Promise<AeCompleteResponseResult> {

    const res = await api.post(`${BASE}/public/${linkToken}/responses/${responseId}/complete`);

    return res.data.data;

  },



  async getInstantResults(linkToken: string, responseId: string): Promise<AeInstantResults> {

    const res = await api.get(`${BASE}/public/${linkToken}/responses/${responseId}/results`);

    return res.data.data;

  },

  async sendCampaignFollowUp(
    campaignId: string,
    audience: 'non_complete' | 'never_started' | 'in_progress' = 'non_complete'
  ): Promise<{ sent: number }> {
    const res = await api.post(`${BASE}/campaigns/${campaignId}/follow-up/send`, { audience });
    return res.data.data;
  },

  async postCompletionInvites(
    linkToken: string,
    responseId: string,
    emails: string[]
  ): Promise<{ inserted: number }> {
    const res = await api.post(
      `${BASE}/public/${linkToken}/responses/${responseId}/post-completion/invites`,
      { emails }
    );
    return res.data.data;
  },

  async postCompletionCohortSubscribe(
    linkToken: string,
    responseId: string
  ): Promise<{ subscribed: boolean }> {
    const res = await api.post(
      `${BASE}/public/${linkToken}/responses/${responseId}/post-completion/cohort-subscribe`
    );
    return res.data.data;
  },

  /** Campaign invite / reminder unsubscribe (public, no auth). */
  async unsubscribeInvite(token: string): Promise<{ unsubscribed: boolean }> {
    const res = await api.get(`${BASE}/public/invite-unsubscribe/${encodeURIComponent(token)}`);
    return res.data.data;
  },

  /** Cohort digest unsubscribe (public, no auth). */
  async unsubscribeCohort(token: string): Promise<{ unsubscribed: boolean }> {
    const res = await api.get(`${BASE}/public/cohort-unsubscribe/${encodeURIComponent(token)}`);
    return res.data.data;
  },

  resultsUrl(linkToken: string, responseId: string): string {

    return `/assessment/take/${linkToken}/results/${responseId}`;

  },



  publicTakeUrl(linkToken: string): string {

    return `/assessment/take/${linkToken}`;

  },



  /** Build public take URL from campaign token (client-side fallback when link API unavailable). */

  buildPublicUrl(linkToken: string): string {

    return `${window.location.origin}${this.publicTakeUrl(linkToken)}`;

  },

};


